/* ===========================================================
   Router do SITE PÚBLICO (landing + demo) — ADR §38.
   Clone do router do app SEM /login, /criar-conta e /admin (D1/D3).
   A "demo" é a experiência mock do produto: entra pela landing (/) ou pelo
   gate (/demo) → persona → tour guiado (driver.js, sobre /app/*) → /app/* (telas
   com dados de exemplo). Sem backend, sem auth real. Telas/adaptadores são cópia
   do app (a partir do §38 divergem — clone congelado).
   =========================================================== */
import { Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useDemo } from "./DemoProvider";
import { lazyWithReload } from "./lazy-with-reload";
import { RouteFallback } from "../components/route-fallback";
import { RouteError } from "../components/route-error";
import type { FiltroCatalogo } from "../screens/catalogo";
import { Landing } from "../screens/landing";
import { TermosDeUso, PoliticaPrivacidade } from "../screens/legal";

// Fase 2 (app real on-prem): VITE_APP_URL preenchido → "Entrar" vai ao app real.
// Vazio → "Entrar" entra na própria demo.
const APP_URL = (import.meta.env.VITE_APP_URL as string | undefined)?.replace(/\/$/, "") ?? "";

// Layout da demo (/app/*) — lazy: puxa DemoFlow + driver.js (o tour), que NÃO
// devem pesar no bundle da landing/conteúdo público. Só carrega ao entrar na demo.
const AppLayout = lazyWithReload(() =>
  import("./AppLayout").then((m) => ({ default: m.AppLayout })),
);

const Dashboard = lazyWithReload(() =>
  import("../screens/dashboard").then((m) => ({ default: m.Dashboard })),
);
const Diagnostico = lazyWithReload(() =>
  import("../screens/diagnostico").then((m) => ({ default: m.Diagnostico })),
);
const CurvaABC = lazyWithReload(() =>
  import("../screens/curva-abc").then((m) => ({ default: m.CurvaABC })),
);
const Pedidos = lazyWithReload(() =>
  import("../screens/pedidos").then((m) => ({ default: m.Pedidos })),
);
const GestaoProdutos = lazyWithReload(() =>
  import("../screens/catalogo").then((m) => ({ default: m.GestaoProdutos })),
);
const MarketingVisaoGeral = lazyWithReload(() =>
  import("../screens/marketing").then((m) => ({ default: m.MarketingVisaoGeral })),
);
const MarketingProdutos = lazyWithReload(() =>
  import("../screens/marketing").then((m) => ({ default: m.MarketingProdutos })),
);
const MarketingIntegracoes = lazyWithReload(() =>
  import("../screens/marketing").then((m) => ({ default: m.MarketingIntegracoes })),
);
const RecompraLTV = lazyWithReload(() =>
  import("../screens/recompra-ltv").then((m) => ({ default: m.RecompraLTV })),
);
const Assinatura = lazyWithReload(() =>
  import("../screens/assinatura").then((m) => ({ default: m.Assinatura })),
);
const Conta = lazyWithReload(() => import("../screens/conta").then((m) => ({ default: m.Conta })));
const Notificacoes = lazyWithReload(() =>
  import("../screens/notificacoes").then((m) => ({ default: m.Notificacoes })),
);

/* ---------- conteúdo público (cluster de SEO) — lazy, fora do bundle da home ---------- */
const Pilar = lazyWithReload(() =>
  import("../screens/conteudo").then((m) => ({ default: m.Pilar })),
);
const GlossarioTermo = lazyWithReload(() =>
  import("../screens/conteudo").then((m) => ({ default: m.GlossarioTermo })),
);
const BlogIndex = lazyWithReload(() =>
  import("../screens/conteudo").then((m) => ({ default: m.BlogIndex })),
);
const Artigo = lazyWithReload(() =>
  import("../screens/conteudo").then((m) => ({ default: m.Artigo })),
);
const CalculadoraRoas = lazyWithReload(() =>
  import("../screens/conteudo").then((m) => ({ default: m.CalculadoraRoas })),
);
const CalculadoraMargem = lazyWithReload(() =>
  import("../screens/conteudo").then((m) => ({ default: m.CalculadoraMargem })),
);

function GlossarioTermoRoute() {
  const { slug } = useParams();
  return <GlossarioTermo slug={slug} />;
}

function ArtigoRoute() {
  const { slug } = useParams();
  return <Artigo slug={slug} />;
}

/* ---------- raiz: scroll-to-top + Suspense (sem DevPanel/ImpersonationBar) ---------- */
function Root() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  );
}

function LandingRoute({ abrirDemoInicial = false }: { abrirDemoInicial?: boolean }) {
  const navigate = useNavigate();
  const { setDemoFase } = useDemo();
  // CTA da demo → vai pro app; o gate aparece SOBRE o painel borrado (jornada nova).
  const irDemo = () => {
    setDemoFase("gate");
    navigate("/app/inicio");
  };
  return (
    <Landing
      abrirDemoInicial={abrirDemoInicial}
      onEntrar={() => {
        if (APP_URL) window.location.href = `${APP_URL}/login`;
        else irDemo();
      }}
      onIrDemo={irDemo}
    />
  );
}

function TermosRoute() {
  const navigate = useNavigate();
  return <TermosDeUso onVoltar={() => navigate("/")} />;
}

function PrivacidadeRoute() {
  const navigate = useNavigate();
  return <PoliticaPrivacidade onVoltar={() => navigate("/")} />;
}

function AssinaturaRoute() {
  const navigate = useNavigate();
  const { trial, setTrial } = useDemo();
  return (
    <Assinatura
      assinante={trial === "subscribed"}
      onConfirmar={() => {
        setTrial("subscribed");
        navigate("/app/inicio");
      }}
      onVoltar={() => navigate(-1)}
    />
  );
}

function DashboardRoute() {
  const navigate = useNavigate();
  const { mediaConn } = useDemo();
  return (
    <Dashboard
      goDiagnostico={() => navigate("/app/diagnostico")}
      mediaConn={mediaConn}
      onConectar={() => navigate("/app/mkt-integracoes")}
    />
  );
}

function ProdutosRoute() {
  const [params] = useSearchParams();
  const filtro = params.get("filtro");
  const filtroInicial: FiltroCatalogo = filtro === "sem-custo" ? filtro : "todos";
  return <GestaoProdutos filtroInicial={filtroInicial} />;
}

function MarketingVisaoRoute() {
  const navigate = useNavigate();
  const { mediaConn } = useDemo();
  return (
    <MarketingVisaoGeral
      mediaConn={mediaConn}
      onConectar={() => navigate("/app/mkt-integracoes")}
    />
  );
}

function MarketingProdutosRoute() {
  const navigate = useNavigate();
  const { mediaConn } = useDemo();
  return (
    <MarketingProdutos mediaConn={mediaConn} onConectar={() => navigate("/app/mkt-integracoes")} />
  );
}

function RecompraRoute() {
  const navigate = useNavigate();
  const { mediaConn } = useDemo();
  return <RecompraLTV mediaConn={mediaConn} onConectar={() => navigate("/app/mkt-integracoes")} />;
}

function IntegracoesRoute() {
  const { mediaConn, setMediaConn } = useDemo();
  return <MarketingIntegracoes mediaConn={mediaConn} setMediaConn={setMediaConn} />;
}

function ContaRoute() {
  const navigate = useNavigate();
  const { user, setUser, team, setTeam, trial } = useDemo();
  return (
    <Conta
      user={user}
      setUser={setUser}
      team={team}
      setTeam={setTeam}
      trial={trial}
      onAssinar={() => navigate("/assinatura")}
    />
  );
}

/* ---------- mapa de rotas ---------- */
export const routes = [
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: "/", element: <LandingRoute /> },
      // deep-link da demo (ex.: anúncios) → landing com o popup do gate aberto.
      { path: "/demo", element: <LandingRoute abrirDemoInicial /> },
      // URL legada de waitlist → abre a demo (preserva links de anúncios/SEO).
      { path: "/lista-de-espera", element: <Navigate to="/demo" replace /> },
      { path: "/termos", element: <TermosRoute /> },
      { path: "/privacidade", element: <PrivacidadeRoute /> },
      // Conteúdo público (cluster de SEO). Indexável + prerenderizado.
      // ⚠ Manter em sincronia com scripts/rotas-publicas.mjs (prerender + sitemap).
      { path: "/lucro-real-ecommerce", element: <Pilar /> },
      // Glossário deixou de ser seção própria — os conceitos vivem no blog.
      { path: "/glossario", element: <Navigate to="/blog" replace /> },
      { path: "/glossario/:slug", element: <GlossarioTermoRoute /> },
      { path: "/blog", element: <BlogIndex /> },
      { path: "/blog/:slug", element: <ArtigoRoute /> },
      { path: "/calculadora/roas-de-equilibrio", element: <CalculadoraRoas /> },
      { path: "/calculadora/margem-de-contribuicao", element: <CalculadoraMargem /> },
      { path: "/assinatura", element: <AssinaturaRoute /> },
      {
        path: "/app",
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/app/inicio" replace /> },
          { path: "inicio", element: <DashboardRoute /> },
          { path: "diagnostico", element: <Diagnostico /> },
          { path: "acoes", element: <Navigate to="/app/diagnostico" replace /> },
          { path: "lucro", element: <Navigate to="/app/inicio" replace /> },
          { path: "produtos", element: <ProdutosRoute /> },
          { path: "curva-abc", element: <CurvaABC /> },
          { path: "pedidos", element: <Pedidos /> },
          { path: "mkt-visao", element: <MarketingVisaoRoute /> },
          { path: "mkt-produtos", element: <MarketingProdutosRoute /> },
          { path: "mkt-recompra", element: <RecompraRoute /> },
          { path: "mkt-integracoes", element: <IntegracoesRoute /> },
          { path: "conta", element: <ContaRoute /> },
          { path: "notificacoes", element: <Notificacoes /> },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
